import { FocusableElement } from '@react-types/shared';
import React from 'react';
import { mergeProps } from 'react-aria';
import useLink from 'hooks/useLink';
import useMergedRefs from 'hooks/useMergedRefs';
import * as S from './styles';
import { LinkProps } from './types';
import { extractProps, getTypographyComponent } from './utils';

export function ForwardedLink<
  T extends FocusableElement = HTMLAnchorElement,
  As extends React.ElementType = 'a',
>(props: LinkProps<As>, forwardedRef: React.ForwardedRef<T>) {
  const { componentProps } = extractProps(props);
  const { as, children, className, typography, variant, ...restProps } =
    componentProps;

  const {
    linkProps: { ref: linkRef, ...linkProps },
  } = useLink<T>(props);

  const TypographyComponent = getTypographyComponent(props);
  const Component: React.ElementType = as ?? 'a';

  const ref = useMergedRefs<T>([linkRef, forwardedRef]);

  const mergedProps = mergeProps(restProps, linkProps);

  const asComponent: React.ElementType = TypographyComponent ?? Component;
  const forwardedAsComponent: React.ElementType | undefined =
    asComponent === Component ? undefined : Component;

  return (
    <S.Root
      as={asComponent}
      forwardedAs={forwardedAsComponent}
      typography={typography}
      variant={!typography ? 'inherit' : variant}
      {...{ className, ref }}
      {...mergedProps}
    >
      {children}
    </S.Root>
  );
}

/**
 * A link component used to navigate between pages.
 *
 * `Link` is based on `Typography`, so any typography variants can be used.  In
 * order to use `Text` variants, set `props.typography = 'text'` along with
 * the corresponding `Text` component props.  Similarly, to use `Heading` variants,
 * set `props.typography = 'heading'` along with the corresponding `Heading` props.
 *
 * Often, a Link is used within another `Typography` component or inside of a block
 * that otherwise has its own text styles.  The `Link` can inherit the contain block's
 * text styles by leaving `props.typography` unset and setting `props.variant = 'inherit'`.
 *
 * ### External Links
 * When linking externally from an app, `Link` can be used without much modification.
 *
 * ### Internal Links
 * For interal routes, the `Link` can be customized. For example, to render a link
 * using `react-router`'s `Link`:
 *
 * ```
 * <Link as={ReactRouterLink} elementType="a" to="/pathname">
 *  click me!
 * </Link>
 * ```
 *
 * ### Button Links
 * Sometimes, a design calls for an element that looks like a link, but is actually
 * a button.  In that case, the `Link` can be rendered as follows:
 *
 * ```
 * <Link as="button" elementType="button" onPress={() => alert('hi')}>
 *  click me!
 * </Link>
 * ```
 */
export const Link = React.forwardRef(ForwardedLink) as <
  T extends FocusableElement = HTMLAnchorElement,
  As extends React.ElementType = 'a',
>(
  props: LinkProps<As> & { ref?: React.Ref<T> },
) => React.ReactElement | null;

export default Link;
